/**
 * Calendar primitives for Progear Finance 2.0, module 1.1 (the primary view's
 * "Kalender" lens).
 *
 * This is the ONE definition of (a) the Estonian public-holiday calendar and the
 * working-day shift derived from it, and (b) how a persisted cash-flow entry is
 * classified as a statutory tax declaration (KMD / TSD). Both are domain rules,
 * so they live in finance-core (replit.md master rule: one truth per concept,
 * never reinvented in a component) and are golden-tested.
 *
 * No money is computed here (L9): tax AMOUNTS already live on the persisted
 * entries (seed §4 / the matrix KM + TSD footers via `vatPosition` / `payrollTax`)
 * — `taxKindOf` only LABELS an existing entry so the calendar can badge it. The
 * holiday helpers are pure date arithmetic: ISO calendar days in, ISO days out,
 * no I/O and no `Date.now()` (the caller passes the date), so results are
 * reproducible and golden-testable.
 */

import { addDays } from './cashflow';
import type { CashFlowEntry } from './cashflow';

/** A single public holiday (riigipüha). `date` is an ISO calendar day. */
export interface Holiday {
  /** ISO calendar day, `YYYY-MM-DD`. */
  date: string;
  /** Estonian holiday name (UI label). */
  name: string;
  /** Country the holiday belongs to (only Estonia for now). */
  country: 'EE';
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function assertISO(value: string, label: string): void {
  if (!ISO_DATE.test(value)) {
    throw new RangeError(`${label}: expected an ISO date (YYYY-MM-DD), received ${value}`);
  }
}

/**
 * Estonian public holidays for 2026 (riigipühad) — the ONE source for both the
 * calendar's holiday badge and the working-day shift. Mirrors the approved canvas
 * (CashFlowCalendar.tsx → EE_HOLIDAYS_2026). When the app rolls into a new year,
 * the next year's set is added here, not recomputed in a component.
 */
export const EE_HOLIDAYS_2026: readonly Holiday[] = [
  { date: '2026-01-01', name: 'Uusaasta', country: 'EE' },
  { date: '2026-02-24', name: 'Iseseisvuspäev', country: 'EE' },
  { date: '2026-04-03', name: 'Suur reede', country: 'EE' },
  { date: '2026-04-05', name: 'Ülestõusmispühad', country: 'EE' },
  { date: '2026-05-01', name: 'Kevadpüha', country: 'EE' },
  { date: '2026-05-24', name: 'Nelipühad', country: 'EE' },
  { date: '2026-06-23', name: 'Võidupüha', country: 'EE' },
  { date: '2026-06-24', name: 'Jaanipäev', country: 'EE' },
  { date: '2026-08-20', name: 'Taasiseseisvumispäev', country: 'EE' },
  { date: '2026-12-24', name: 'Jõululaupäev', country: 'EE' },
  { date: '2026-12-25', name: 'Esimene jõulupüha', country: 'EE' },
  { date: '2026-12-26', name: 'Teine jõulupüha', country: 'EE' },
];

/** The public holiday falling on `date`, or null if it is an ordinary day. */
export function holidayOn(
  date: string,
  holidays: readonly Holiday[] = EE_HOLIDAYS_2026,
): Holiday | null {
  assertISO(date, 'date');
  return holidays.find((h) => h.date === date) ?? null;
}

/** Day of week for an ISO day: 0 = Sunday … 6 = Saturday (UTC, DST-safe). */
function dayOfWeek(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** True when `date` is a working day — neither a weekend nor a public holiday. */
export function isWorkingDay(
  date: string,
  holidays: readonly Holiday[] = EE_HOLIDAYS_2026,
): boolean {
  assertISO(date, 'date');
  const dow = dayOfWeek(date);
  if (dow === 0 || dow === 6) return false;
  return holidayOn(date, holidays) === null;
}

/**
 * The first working day on or after `date`, skipping weekends and public
 * holidays — the statutory "deadline shifts to the next working day" rule. Pure
 * and always terminating (a finite holiday set can never block every day).
 */
export function firstWorkingDay(
  date: string,
  holidays: readonly Holiday[] = EE_HOLIDAYS_2026,
): string {
  assertISO(date, 'date');
  let cur = date;
  while (!isWorkingDay(cur, holidays)) {
    cur = addDays(cur, 1);
  }
  return cur;
}

/**
 * The last working day on or before `date`, skipping weekends and public
 * holidays backwards — the wage-payment rule: when the contractual payday falls
 * on a weekend/holiday, wages are paid EARLY (the preceding working day), unlike
 * statutory declaration deadlines which shift forward (`firstWorkingDay`). Pure
 * and always terminating for the same reason as its forward twin.
 */
export function lastWorkingDay(
  date: string,
  holidays: readonly Holiday[] = EE_HOLIDAYS_2026,
): string {
  assertISO(date, 'date');
  let cur = date;
  while (!isWorkingDay(cur, holidays)) {
    cur = addDays(cur, -1);
  }
  return cur;
}

/** A statutory tax declaration kind: VAT return (KMD) or payroll tax (TSD). */
export type TaxKind = 'KMD' | 'TSD';

/**
 * Classify a persisted cash-flow entry as a statutory tax declaration, or null
 * when it is an ordinary movement. The ONE truth for "is this a tax, and which"
 * (reused by the calendar's KMD/TSD badge and its tax-specific hover text), keyed
 * off the structural attributes the data model already carries — NOT brittle
 * party strings:
 *  - KMD (käibemaks / VAT return): a `tax_agreement` line from the `tax` source.
 *  - TSD (palgamaksud / payroll tax): a `payroll` line from the `tax` source.
 * A net-wage `payroll` row (source `manual`) and every non-tax line return null.
 */
export function taxKindOf(entry: Pick<CashFlowEntry, 'category' | 'source'>): TaxKind | null {
  if (entry.source !== 'tax') return null;
  if (entry.category === 'tax_agreement') return 'KMD';
  if (entry.category === 'payroll') return 'TSD';
  return null;
}
